<script lang="ts" setup>
import { DefinitionDto, DefinitionParameterValueSetDto } from '@/api/generator';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import { useUnitsStore } from '@/stores/unitsStore.ts';
import { translateEntityProperty } from '@/utils/helper';
import { computed, onMounted, ref, watch } from 'vue';
import { toDisplayValue } from '@/utils/numberUtils.ts';

import { useTranslation } from 'i18next-vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
import { useProtocolsStore } from '@/stores/protocolsStore';
const protocolsStore = useProtocolsStore();

const { t } = useTranslation();

interface ParameterSetTableProps {
  selectedDefinition?: DefinitionDto | null | undefined;
  selectedValueSetId?: string;
  showCheckBoxes?: boolean;
  displayedValueSet?: DefinitionParameterValueSetDto;
}

const props = defineProps<ParameterSetTableProps>();

const emits = defineEmits<{
  onDefinitionParameterValueSetSelected: [
    selectedDefinitionParameterValueSet: DefinitionParameterValueSetDto,
  ];
}>();

const unitsStore = useUnitsStore();
const selectedIndex = ref();

watch(
  () => props.selectedDefinition,
  () => (selectedIndex.value = undefined),
);

watch(
  () => props.selectedValueSetId,
  () => {
    if (!props.selectedValueSetId) return;
    selectedIndex.value = props.selectedDefinition?.definitionParameterValueSets?.findIndex(
      (item) => item.id === props.selectedValueSetId,
    );
  },
);

onMounted(async () => {
  await unitsStore.initialize();
  await protocolsStore.initialize();
  if (!props.selectedValueSetId) return;
  selectedIndex.value = props.selectedDefinition?.definitionParameterValueSets?.findIndex(
    (item) => item.id === props.selectedValueSetId,
  );
});

watch(selectedIndex, (value: number) => {
  const selectedDefinitionParameterValueSet =
    props.selectedDefinition?.definitionParameterValueSets?.[value];

  if (selectedDefinitionParameterValueSet) {
    emits('onDefinitionParameterValueSetSelected', selectedDefinitionParameterValueSet);
  }
});

// table headers
const headers = computed(() => [
  ...(props.showCheckBoxes ? [{ width: 20, key: 'checkBox', title: '', sortable: false }] : []),

  ...(props.selectedDefinition?.definitionParameters?.map((param) => {
    return {
      title: translateEntityProperty('name', param) || '',
      key: param.parameterIdentifier || '',
      sortable: false,
      unitAbbreviation: unitsStore.getById(param.unitId)?.abbreviation,
      displayDecimalPlaces: param.decimalPlaces,
      valueType: param.valueType,
    };
  }) || []),

  {
    key: 'protocol',
    title: t('DefinitionParameterProtocol_one'),
    sortable: false,
  },
]);

function getTableContents(valueSets: DefinitionParameterValueSetDto[] | null | undefined) {
  const returnTable: Array<any> = [];

  const getRow = (valueSet: DefinitionParameterValueSetDto) => {
    const ro: any = {};
    valueSet.values?.forEach((value) => {
      ro[value.parameterIdentifier] = { ...value };
    });

    return ro;
  };

  valueSets?.forEach((valueSet) => {
    if (props.displayedValueSet) {
      if (props.displayedValueSet.id === valueSet.id) {
        returnTable.push(getRow(valueSet));
      }
    } else {
      returnTable.push(getRow(valueSet));
    }
  });

  return returnTable;
}

const tableContents = computed<Array<any>>(() => {
  return getTableContents(props.selectedDefinition?.definitionParameterValueSets);
});
</script>
<template>
  <v-radio-group v-model="selectedIndex">
    <OverviewTable
      :items="tableContents"
      :headers="headers"
      :zebra="true"
      :noSpacing="true"
      class="text-right"
    >
      <!-- template for table cell -->

      <template
        v-for="(header, headerIndex) in headers"
        #[`item.${header.key}`]="{ item, index }"
        :key="`${header.key}${headerIndex}`"
      >
        <div
          v-if="header.key !== 'protocol' && header.key !== 'checkBox'"
          class="item-class"
          @click="() => (selectedIndex = index)"
        >
          {{
            toDisplayValue(item[header.key]?.value, {
              valueType: 'valueType' in header ? header.valueType : undefined,
              fractionDigits:
                'displayDecimalPlaces' in header ? header.displayDecimalPlaces : undefined,
              unitAbbreviation:
                'unitAbbreviation' in header ? header['unitAbbreviation'] : undefined,
            })
          }}
        </div>

        <div v-else-if="header.key === 'protocol'" class="text-left">
          <template v-if="selectedDefinition?.definitionParameterValueSets?.length">
            {{
              protocolsStore.getById(
                selectedDefinition?.definitionParameterValueSets[index].protocolId,
              )?.name || EMPTY_FIELD_SIGN
            }}
          </template>
        </div>

        <div v-else class="item-class">
          <v-radio :value="index" />
        </div>
      </template>
    </OverviewTable>
  </v-radio-group>
</template>

<style scoped lang="scss">
.item-class {
  height: 100%;
  cursor: pointer;
}
</style>
